
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SMSService } from 'src/app/services/sms.service';

@Component({
  selector: 'app-sms-tests',
  templateUrl: './sms-tests.component.html',
  styleUrls: ['./sms-tests.component.scss']
})
export class SMSTestsComponent implements OnInit {

  smsForm!: FormGroup;
  variableForm!: FormGroup;
  status: 'idle' | 'sending' | 'success' | 'error' = 'idle';
  response: any = null;
  errorMessage: string | null = null;

  templates = [
    {
      id: 'sms-invoice.template.html',
      name: 'Invoice ready',
      description: 'Send invoice or receipt details with a friendly handoff.',
      preview: 'Hi {toName}, your invoice is ready on {date}. Balance {amountDue}.',
      variables: {
        toName: 'Summer',
        date: '20 Jul',
        amountDue: '$180.00'
      }
    },
    {
      id: 'sms-review-request.template.html',
      name: 'Google review request',
      description: 'Follow-up asking for a quick Google review.',
      preview: 'Hi {toName}, could you leave us a quick Google review? {reviewLink}',
      variables: {
        toName: 'Summer',
        reviewLink: 'https://g.page/r/CR-ekYinBCYvEAg/review'
      }
    },
    {
      id: 'sms-review-request-aged.template.html',
      name: 'Google review request (aged)',
      description: 'Re-engage customers you haven’t contacted in a while to request a review.',
      preview: 'Hi {toName}, it’s been a while since we helped you. Would you mind leaving a Google review? {reviewLink}',
      variables: {
        toName: 'Summer',
        reviewLink: 'https://g.page/r/CR-ekYinBCYvEAg/review'
      }
    },
    {
      id: 'sms-repair-ready.template.html',
      name: 'Repair ready',
      description: 'Notify a customer that their repair is ready to collect.',
      preview: 'Hi {toName}, your repair ticket {ticketNumber} is ready. Pick up by {readyBy}.',
      variables: {
        toName: 'Summer',
        ticketNumber: 'RB-2044',
        readyBy: '5pm today'
      }
    }
  ];

  constructor(private smsService: SMSService, private formBuilder: FormBuilder) { }

  ngOnInit(): void {
    const defaultTemplate = this.templates[0];
    this.smsForm = this.formBuilder.group({
      to: ['+61466040720', [Validators.required]],
      from: ['Repair Lab', [Validators.required]],
      type: ['test', Validators.required],
      template: [defaultTemplate.id, Validators.required]
    });

    this.setTemplateVariables(defaultTemplate);
  }

  get selectedTemplate() {
    return this.templates.find(t => t.id === this.smsForm?.get('template')?.value);
  }

  get variableKeys(): string[] {
    return this.variableForm ? Object.keys(this.variableForm.controls) : [];
  }

  get payloadPreview() {
    return {
      ...this.smsForm.value,
      templateVariables: this.variableForm?.value
    };
  }

  get previewCopy(): string {
    const template = this.selectedTemplate;
    if (!template || !this.variableForm) {
      return '';
    }

    return template.preview.replace(/\{(\w+)\}/g, (_: string, key: string) => {
      return this.variableForm.get(key)?.value ?? `{${key}}`;
    });
  }

  onTemplateChange(templateId: string) {
    const template = this.templates.find(t => t.id === templateId);
    if (template) {
      this.setTemplateVariables(template);
    }
  }

  setTemplateVariables(template: any) {
    const controls: any = {};
    Object.entries(template.variables).forEach(([key, value]) => {
      controls[key] = [value, Validators.required];
    });
    this.variableForm = this.formBuilder.group(controls);
  }

  useSandboxValues() {
    const template = this.templates[0];
    this.smsForm.patchValue({
      to: '+61466040720',
      from: 'Repair Lab',
      type: 'test',
      template: template.id
    });
    this.setTemplateVariables(template);
  }

  sendTestSMS() {
    if (this.smsForm.invalid || this.variableForm.invalid) {
      this.smsForm.markAllAsTouched();
      this.variableForm.markAllAsTouched();
      return;
    }

    this.status = 'sending';
    this.errorMessage = null;
    this.response = null;

    const payload = {
      ...this.smsForm.value,
      templateVariables: this.variableForm.value
    };

    this.smsService.sendSMS(payload).subscribe({
      next: (r: any) => {
        this.status = 'success';
        this.response = r;
      },
      error: (err: any) => {
        this.status = 'error';
        this.errorMessage = err?.error?.message || 'Failed to send SMS. Check the gateway and payload.';
      }
    });
  }

}
